import React, { useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// ---------- Hero video upload field ----------
// Uploads MP4 (required) + WebM (optional) to /admin/upload-video. Sanity
// caps (3 MB soft warn / 15 MB hard block) mirror the backend, but the soft
// warn is a judgment prompt — Calvin can still ship the file if he chooses.
// A live preview autoplays right in the admin panel so he doesn't have to
// visit the public homepage to verify a new hero looks right.
//
// Field is reusable across heroes (Homepage + About) — pass the CMS key
// prefix so it drives the correct settings tuple. Defaults preserve the
// Homepage behavior so existing callers work unchanged.
export const HeroVideoField = ({
  form,
  setForm,
  enabledKey = "hero_video_enabled",
  mp4Key = "hero_video_mp4",
  webmKey = "hero_video_webm",
  posterKey = null,  // when null → fall back to hero_image_desktop / hero_image
  posterHint = "Mobile poster · Video poster · Fallback if video fails",
  hideEnabledToggle = false,  // About-hero uses a separate media-type select
}) => {
  const [uploading, setUploading] = useState(null); // "mp4" | "webm" | null
  const enabled = !!form[enabledKey];
  const posterImage = posterKey ? (form[posterKey] || "") : (form.hero_image_desktop || form.hero_image || "");
  const mp4 = form[mp4Key] || "";
  const webm = form[webmKey] || "";

  const SOFT_LIMIT = 3 * 1024 * 1024;
  const HARD_LIMIT = 15 * 1024 * 1024;
  const ACCEPTED_MIMES = { mp4: "video/mp4", webm: "video/webm" };

  const handleFile = async (kind, file) => {
    if (!file) return;
    const expected = ACCEPTED_MIMES[kind];
    if (file.type !== expected) {
      toast.error(`Please choose a ${expected} file`);
      return;
    }
    if (file.size > HARD_LIMIT) {
      toast.error(`File is ${(file.size / (1024*1024)).toFixed(1)} MB — hard limit is ${HARD_LIMIT/(1024*1024)} MB. Please compress before uploading.`);
      return;
    }
    if (file.size > SOFT_LIMIT) {
      const mb = (file.size / (1024*1024)).toFixed(1);
      const ok = window.confirm(`This video is ${mb} MB. Videos over ${SOFT_LIMIT/(1024*1024)} MB may slow page load on slower connections. Upload anyway?`);
      if (!ok) return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm({ ...form, [kind === "mp4" ? mp4Key : webmKey]: url });
      toast.success(`${kind.toUpperCase()} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const clearUrl = (key) => setForm({ ...form, [key]: "" });

  return (
    <div className="space-y-4">
      {/* Toggle — hidden for the About hero where a separate media-type
          select determines whether the video path is active. */}
      {!hideEnabledToggle && (
      <label className="flex items-center gap-3 rounded-xl border border-[#262626] bg-[#141414] px-4 py-3 cursor-pointer hover:border-[#FF6600]/40 transition-colors">
        <input
          data-testid={`settings-${enabledKey.replace(/_/g, "-")}`}
          type="checkbox"
          checked={enabled}
          onChange={(e) => setForm({ ...form, [enabledKey]: e.target.checked })}
          className="w-4 h-4 accent-[#FF6600]"
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Enable video hero</div>
          <div className="text-[11px] text-[#A3A3A3] mt-0.5">
            Off = static image on every viewport. On + MP4 uploaded = video on desktop AND mobile (static image stays as the poster + fallback).
          </div>
        </div>
      </label>
      )}

      {/* Mobile / fallback reminder */}
      {posterImage && (
        <div className="rounded-xl border border-[#262626] bg-[#141414] p-3 flex items-center gap-3">
          <img src={posterImage} alt="poster + mobile + fallback" className="w-16 h-16 rounded-lg object-cover bg-[#0F0F0F]" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6600]">Also used as</div>
            <div className="text-xs text-white mt-0.5">{posterHint}</div>
            <div className="text-[11px] text-[#7A7A7A] mt-0.5 truncate">{posterImage}</div>
          </div>
        </div>
      )}

      {/* MP4 upload */}
      <VideoSlot
        kind="mp4"
        testIdPrefix={mp4Key}
        label="MP4 (H.264) — required"
        url={mp4}
        uploading={uploading === "mp4"}
        onFile={(f) => handleFile("mp4", f)}
        onClear={() => clearUrl(mp4Key)}
        posterImage={posterImage}
      />

      {/* WebM upload */}
      <VideoSlot
        kind="webm"
        testIdPrefix={webmKey}
        label="WebM (VP9) — optional, served first to browsers that support it"
        url={webm}
        uploading={uploading === "webm"}
        onFile={(f) => handleFile("webm", f)}
        onClear={() => clearUrl(webmKey)}
        posterImage={posterImage}
      />

      {!enabled && (mp4 || webm) && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] px-4 py-3 text-xs text-amber-200">
          Video files uploaded but the toggle is <b>off</b> — the static image will still be shown. Turn on <b>Enable video hero</b> above to go live.
        </div>
      )}
      {enabled && !mp4 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] px-4 py-3 text-xs text-amber-200">
          Toggle is on but no MP4 is uploaded — the site will fall back to the static image on every viewport until you upload one.
        </div>
      )}
    </div>
  );
};

const VideoSlot = ({ kind, label, url, uploading, onFile, onClear, posterImage, testIdPrefix }) => {
  // Fallback to the legacy Homepage-hero testids when the caller didn't
  // pass an explicit prefix so existing snapshots/tests keep passing.
  const prefix = testIdPrefix ? testIdPrefix.replace(/_/g, "-") : `hero-video-${kind}`;
  return (
    <div className="rounded-xl border border-[#262626] bg-[#141414] p-4">
      <div className="text-xs font-semibold text-white">{label}</div>
      {url && (
        <div className="mt-3 space-y-2">
          <video
            data-testid={`settings-${prefix}-preview`}
            src={url}
            poster={posterImage || undefined}
            autoPlay
            muted
            loop
            playsInline
            className="w-full rounded-lg bg-black max-h-56 object-cover"
          />
          <div className="flex items-center gap-2">
            <div className="flex-1 text-[11px] text-[#7A7A7A] truncate">{url}</div>
            <button
              type="button"
              onClick={onClear}
              data-testid={`settings-${prefix}-clear`}
              className="text-xs text-[#FF6600] font-semibold hover:underline shrink-0"
            >
              Clear
            </button>
          </div>
        </div>
      )}
      <label className={`mt-3 flex items-center justify-center gap-2 border border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors ${uploading ? "border-[#FF6600] bg-[#1A1209]" : "border-[#333] hover:border-[#FF6600] hover:bg-[#0F0F0F]"}`}>
        <input
          data-testid={`settings-${prefix}-upload`}
          type="file"
          accept={kind === "mp4" ? "video/mp4" : "video/webm"}
          className="hidden"
          disabled={uploading}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <UploadCloud className="w-4 h-4 text-[#FF6600]" />
        <span className="text-sm font-semibold">
          {uploading ? "Uploading…" : url ? `Replace ${kind.toUpperCase()}` : `Upload ${kind.toUpperCase()}`}
        </span>
      </label>
    </div>
  );
};
